import React from "react"
import { useRender } from "@base-ui/react/use-render"
import { matchesExactPath } from "../navigation/navigation-utils"
import { cn } from "../utils"
import { useTabLinksContext } from "./tab-links-context"

// =============================================================================
// Props
// =============================================================================

export type TabLinksItemProps = {
  /**
   * Link content. Compose an `Icon` before the label or a `CountBadge` after
   * it as needed.
   */
  children?: React.ReactNode
  className?: string
  /**
   * Link href. The item is active when it matches the ancestor `TabLinks`'
   * `currentPath` exactly (a trailing slash on `href` is ignored).
   */
  href: string
  /**
   * Replace the rendered element so consumers can supply their own link
   * component (e.g. Inertia `Link`, react-router `Link`, plain `<a>`).
   * Defaults to an `<a>` when omitted.
   *
   * Pass the content as `children` of the item and a childless render element
   * (`render={<Link href="…" />}`). Children placed directly on the render
   * element take precedence over the item's `children`.
   */
  render?: useRender.RenderProp
}

// =============================================================================
// Styles
// =============================================================================

const tabLinksItemStyles = [
  "relative",
  "border-b border-transparent aria-[current]:border-gray-900",
  "[--tab-links-item-padding-y:1rem] py-(--tab-links-item-padding-y)",
  "inline-flex items-center gap-x-2",
  "text-sm leading-6 font-semibold text-gray-700 text-nowrap aria-[current]:text-gray-900",
  "transition-colors duration-200",
  "hover:border-gray-700 aria-[current]:hover:border-gray-900",
  // Add an inset focus indicator
  "focus-visible:outline-none",
  "before:absolute before:inset-x-[calc(var(--tab-links-focus-indicator-offset)*-1)] before:inset-y-(--tab-links-item-padding-y) before:rounded-sm",
  "before:outline-2 before:outline-transparent before:transition-colors before:duration-200",
  "focus-visible:before:outline-gray-900"
]

// =============================================================================
// Component
// =============================================================================

/**
 * A tab link, rendered as an `<a>` with `aria-current="page"` when its `href`
 * matches the ancestor `TabLinks`' `currentPath`.
 *
 * Must be a descendant of `TabLinks`.
 *
 * To change the vertical padding, set `--tab-links-item-padding-y` (e.g.
 * `[--tab-links-item-padding-y:1.25rem]`) rather than a `py-*` class, so the
 * focus ring stays around the content.
 */
export function TabLinksItem({
  children,
  className,
  href,
  render
}: TabLinksItemProps) {
  const { currentPath } = useTabLinksContext()
  const isActive = matchesExactPath(currentPath, href)

  return useRender({
    defaultTagName: "a",
    render,
    props: {
      "aria-current": isActive ? ("page" as const) : undefined,
      children,
      className: cn(tabLinksItemStyles, className),
      "data-slot": "tab-links-item",
      href
    }
  })
}
